import { createPortal } from "react-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceLine } from "recharts";
import type { TooltipProps } from "recharts";
import { SOURCES } from "./usageColors";
import type { UsageBucket } from "../api/usage";

type Props = {
  buckets: UsageBucket[];
  sourceKeys: string[];
  colors: Record<string, string>;
  granularity: string;
  start: string;
  end: string;
  detailsContainer: HTMLDivElement | null;
};

const formatTick = (bucket: number, granularity: string) => {
  const d = new Date(bucket);
  if (granularity === "5m" || granularity === "1h") {
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric", timeZone: "UTC" });
};

const formatYAxis = (v: number) => {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(Math.abs(v) >= 10_000 ? 0 : 1)}K`;
  return String(v);
};

const UsageTooltip = ({ active, payload, label, daily }: TooltipProps<number, string> & { daily: boolean }) => {
  if (!active || !payload?.length)
    return (
      <div className="usage-details-empty">
        <strong>Activity details</strong>
        <span>Hover over a bar to see its date and source breakdown.</span>
      </div>
    );
  const entries = payload
    .filter((entry) => Number(entry.value) !== 0)
    .sort((a, b) => Math.abs(Number(b.value)) - Math.abs(Number(a.value)));
  const total = entries.reduce((sum, entry) => sum + Number(entry.value), 0);
  const remaining = entries.slice(5);
  const date = new Date(Number(label)).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
    ...(!daily ? { hour: "2-digit" as const, minute: "2-digit" as const } : {}),
  });
  return (
    <div className="usage-tooltip">
      <div className="usage-tooltip-date">{date} · UTC</div>
      <div className="usage-tooltip-total">
        <span>Total</span>
        <strong>{total.toLocaleString()}</strong>
      </div>
      {entries.slice(0, 5).map((entry) => (
        <div className="usage-tooltip-row" key={String(entry.dataKey)}>
          <span>
            <i style={{ background: entry.color }} />
            {SOURCES[String(entry.dataKey)] ?? entry.name}
          </span>
          <span>{Number(entry.value).toLocaleString()}</span>
        </div>
      ))}
      {remaining.length > 0 && (
        <div className="usage-tooltip-more">
          <span>{remaining.length} other sources</span>
          <span>{remaining.reduce((sum, entry) => sum + Number(entry.value), 0).toLocaleString()}</span>
        </div>
      )}
    </div>
  );
};

const UsageChart = ({ buckets, sourceKeys, colors, granularity, start, end, detailsContainer }: Props) => {
  // Aggregate long windows into UTC days and fill empty time intervals.
  const daily = Date.parse(end) - Date.parse(start) > 2 * 86_400_000;
  const interval = daily || granularity === "1d" ? 86_400_000 : granularity === "1h" ? 3_600_000 : 300_000;
  const displayGranularity = daily ? "1d" : granularity;
  const first = Math.floor(Date.parse(start) / interval) * interval;
  const byTime = new Map<number, Record<string, number>>();
  for (let time = first; time < Date.parse(end); time += interval) {
    byTime.set(time, { timestamp: time, ...Object.fromEntries(sourceKeys.map((key) => [key, 0])) });
  }
  for (const bucket of buckets) {
    const time = Math.floor(Date.parse(bucket.start) / interval) * interval;
    const row = byTime.get(time);
    if (!row) continue;
    for (const key of sourceKeys) row[key] += bucket.sources[key]?.count ?? 0;
  }
  const data = [...byTime.values()];

  const isSingle = sourceKeys.length === 1;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 0, right: 0, bottom: 0, left: 0 }} barCategoryGap="20%" stackOffset="sign">
        <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="none" vertical={false} />
        <XAxis
          dataKey="timestamp"
          type="category"
          tickFormatter={(v) => formatTick(Number(v), displayGranularity)}
          minTickGap={32}
          tick={{ fill: "var(--chart-tick)", fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickMargin={8}
        />
        <YAxis
          tickFormatter={formatYAxis}
          tick={{ fill: "var(--chart-tick)", fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickMargin={4}
          width={64}
        />
        <ReferenceLine y={0} stroke="var(--chart-grid)" />
        <Tooltip<number, string>
          cursor={{ fill: "var(--chart-cursor)" }}
          content={(props) =>
            detailsContainer
              ? createPortal(<UsageTooltip {...props} daily={displayGranularity === "1d"} />, detailsContainer)
              : null
          }
          isAnimationActive={false}
          wrapperStyle={{ pointerEvents: "none", zIndex: 10 }}
        />
        {sourceKeys.map((key, i) => (
          <Bar
            key={key}
            dataKey={key}
            stackId={isSingle ? undefined : "stack"}
            fill={colors[key]}
            isAnimationActive={false}
            radius={isSingle ? [2, 2, 0, 0] : i === sourceKeys.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
            maxBarSize={40}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default UsageChart;
